import {
  Page, Layout, Card, Text, BlockStack, InlineGrid, InlineStack, Tabs, Spinner, Banner,
} from '@shopify/polaris';
import { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { apiGet } from '../api';
import KPICard from '../components/KPICard';
import FunnelChart from '../components/FunnelChart'; // v2

// ── Brand colours ──
const COLORS = {
  matcha: '#7E942B',
  gold: '#D89C70',
  darkGreen: '#5A6B1F',
  orange: '#E8A87C',
  grey: '#8C8C8C',
  blue: '#6B9BC3',
  pink: '#C76B8F',
  cream: '#FDFBF7',
};
const PIE_COLORS = [COLORS.matcha, COLORS.gold, COLORS.darkGreen, COLORS.orange, COLORS.blue, COLORS.pink, COLORS.grey];

// ── Types ──
interface OverviewData {
  totalUsers: number;
  activeUsers30d: number;
  totalOrders: number;
  totalRevenue: number;
  aov: number;
  churnRate: number;
  activeSubscriptions: number;
  ritualCompletionsToday: number;
  avgStreak: number;
  totalPointsCirculation: number;
  totalReferrals: number;
  deviceTokenCount: number;
  totalUsers_prev: number;
  activeUsers30d_prev: number;
  totalOrders30d: number;
  totalOrders30d_prev: number;
  totalRevenue30d: number;
  totalRevenue30d_prev: number;
}

interface EngagementData {
  ritualsToday: number;
  rituals7d: number;
  rituals30d: number;
  completionRate: number;
  moodDistribution: { mood: string; count: string }[];
  matchaTypes: { matcha_type: string; count: string }[];
  avgPointsPerRitual: number;
  ritualsPerDay: { date: string; count: string }[];
}

interface StreaksData {
  avgCurrentStreak: number;
  distribution: { bucket: string; count: string }[];
  topStreaks: { customer_id: string; current_streak: number; longest_streak: number; email: string }[];
}

interface PointsData {
  totalCirculation: number;
  totalIssued: number;
  totalRedeemed: number;
  byReason: { reason: string; issued: string; redeemed: string }[];
  pointsPerDay: { date: string; issued: string; redeemed: string }[];
}

interface ReferralsData {
  totalClicks: number;
  statusBreakdown: { status: string; count: string }[];
  funnel: { clicks: string; installed: string; registered: string; purchased: string };
  topReferrers: { email: string; total_referrals: string; conversions: string }[];
}

interface GrowthData {
  period: string;
  data: { period: string; new_users: string }[];
}

interface RewardsStatsData {
  byStatus: { status: string; count: string }[];
  topRewards: { reward_id: string; name: string; redemption_count: string; total_points: string }[];
  totalPointsSpent: number;
}

interface OrdersData {
  period: string;
  data: { period: string; orders: number; revenue: number }[];
}

// ── Helpers ──
function pctChange(current: number, prev: number): number | null {
  if (prev === 0) return current > 0 ? 100 : null;
  return ((current - prev) / prev) * 100;
}

export default function Dashboard() {
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [engagement, setEngagement] = useState<EngagementData | null>(null);
  const [streaksData, setStreaksData] = useState<StreaksData | null>(null);
  const [pointsData, setPointsData] = useState<PointsData | null>(null);
  const [referralsData, setReferralsData] = useState<ReferralsData | null>(null);
  const [growthData, setGrowthData] = useState<GrowthData | null>(null);
  const [ordersData, setOrdersData] = useState<OrdersData | null>(null);
  const [rewardsStats, setRewardsStats] = useState<RewardsStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    Promise.all([
      apiGet<OverviewData>('/panel-api/analytics/overview'),
      apiGet<EngagementData>('/panel-api/analytics/engagement'),
      apiGet<StreaksData>('/panel-api/analytics/streaks'),
      apiGet<PointsData>('/panel-api/analytics/points'),
      apiGet<ReferralsData>('/panel-api/analytics/referrals'),
      apiGet<GrowthData>('/panel-api/analytics/growth?period=day'),
      apiGet<OrdersData>('/panel-api/analytics/orders?period=day'),
      apiGet<RewardsStatsData>('/panel-api/analytics/rewards-stats'),
    ])
      .then(([ov, eng, str, pts, ref, grw, ord, rew]) => {
        setOverview(ov);
        setEngagement(eng);
        setStreaksData(str);
        setPointsData(pts);
        setReferralsData(ref);
        setGrowthData(grw);
        setOrdersData(ord);
        setRewardsStats(rew);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleTabChange = useCallback((index: number) => setSelectedTab(index), []);

  if (loading) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack align="center" inlineAlign="center">
                <Spinner size="large" />
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>Failed to load analytics: {error}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!overview) return null;

  // ── Tab data ──
  const tabs = [
    { id: 'funnel', content: 'App Funnel' },
    { id: 'revenue', content: 'Revenue' },
    { id: 'users', content: 'Users' },
    { id: 'engagement', content: 'Engagement' },
  ];

  // ── Chart renderers ──
  const renderRevenueChart = () => {
    if (!ordersData) return null;
    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={ordersData.data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="period" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="left" />
          <YAxis yAxisId="right" orientation="right" />
          <Tooltip />
          <Legend />
          <Line yAxisId="left" type="monotone" dataKey="revenue" stroke={COLORS.matcha} name="Revenue (pts)" strokeWidth={2} dot={false} />
          <Line yAxisId="right" type="monotone" dataKey="orders" stroke={COLORS.gold} name="Orders" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderUsersChart = () => {
    if (!growthData) return null;
    const chartData = growthData.data.map((d) => ({ period: d.period, newUsers: Number(d.new_users) }));
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="period" tick={{ fontSize: 11 }} />
          <YAxis />
          <Tooltip />
          <Bar dataKey="newUsers" fill={COLORS.matcha} name="New Users" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderEngagementChart = () => {
    if (!engagement) return null;
    const chartData = engagement.ritualsPerDay.map((d) => ({ date: d.date, rituals: Number(d.count) }));
    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis />
          <Tooltip />
          <Line type="monotone" dataKey="rituals" stroke={COLORS.matcha} name="Rituals" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderFunnelTab = () => <FunnelChart />;
  const tabContent = [renderFunnelTab, renderRevenueChart, renderUsersChart, renderEngagementChart];

  // ── Mood pie data ──
  const moodData = (engagement?.moodDistribution ?? []).map((m) => ({
    name: m.mood,
    value: Number(m.count),
  }));

  // ── Matcha types bar ──
  const matchaData = (engagement?.matchaTypes ?? []).map((m) => ({
    name: m.matcha_type,
    value: Number(m.count),
  }));

  // ── Streaks distribution ──
  const streaksDist = (streaksData?.distribution ?? []).map((d) => ({
    bucket: d.bucket,
    count: Number(d.count),
  }));

  // ── Points by reason ──
  const pointsByReason = (pointsData?.byReason ?? []).map((r) => ({
    reason: r.reason,
    issued: Number(r.issued),
    redeemed: Number(r.redeemed),
  }));

  // ── Referrals funnel ──
  const funnelData = referralsData?.funnel
    ? [
        { stage: 'Clicks', value: Number(referralsData.funnel.clicks) },
        { stage: 'Installed', value: Number(referralsData.funnel.installed) },
        { stage: 'Registered', value: Number(referralsData.funnel.registered) },
        { stage: 'Purchased', value: Number(referralsData.funnel.purchased) },
      ]
    : [];

  // ── Top rewards ──
  const topRewardsData = (rewardsStats?.topRewards ?? []).map((r) => ({
    name: r.name.length > 20 ? r.name.slice(0, 20) + '…' : r.name,
    count: Number(r.redemption_count),
  }));

  return (
    <Page title="Dashboard" subtitle="Matcha & CO Overview">
      <Layout>
        {/* ── Section 1: KPIs ── */}
        <Layout.Section>
          <BlockStack gap="400">
            <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
              <KPICard
                title="Total Users"
                value={overview.totalUsers}
                change={pctChange(overview.totalUsers, overview.totalUsers_prev)}
              />
              <KPICard
                title="Active Users (30d)"
                value={overview.activeUsers30d}
                change={pctChange(overview.activeUsers30d, overview.activeUsers30d_prev)}
              />
              <KPICard
                title="Installs (devices)"
                value={overview.deviceTokenCount}
              />
              <KPICard
                title="Revenue (30d)"
                value={`€${overview.totalRevenue30d.toLocaleString()}`}
                change={pctChange(overview.totalRevenue30d, overview.totalRevenue30d_prev)}
              />
            </InlineGrid>
            <InlineGrid columns={{ xs: 1, sm: 2, md: 5 }} gap="400">
              <KPICard
                title="AOV"
                value={`€${overview.aov.toFixed(2)}`}
              />
              <KPICard
                title="Rituals Today"
                value={overview.ritualCompletionsToday}
              />
              <KPICard
                title="Avg Streak"
                value={overview.avgStreak}
              />
              <KPICard
                title="Active Subscriptions"
                value={overview.activeSubscriptions}
              />
              <KPICard
                title="Points in Circulation"
                value={overview.totalPointsCirculation.toLocaleString()}
              />
            </InlineGrid>
          </BlockStack>
        </Layout.Section>

        {/* ── Section 2: Activity charts (tabs) ── */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Activity</Text>
              <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}>
                <div style={{ padding: '16px 0' }}>
                  {tabContent[selectedTab]?.()}
                </div>
              </Tabs>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* ── Section 3: Engagement (moods + matcha types) ── */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Mood Distribution (30d)</Text>
                {moodData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={moodData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ name, percent }: any) => `${name ?? ''} ${((percent ?? 0) * 100).toFixed(0)}%`}
                      >
                        {moodData.map((_entry, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Text as="p" tone="subdued">No mood data yet</Text>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Top Matcha Types (30d)</Text>
                {matchaData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={matchaData} layout="vertical" margin={{ left: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                      <Tooltip />
                      <Bar dataKey="value" fill={COLORS.gold} name="Count" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Text as="p" tone="subdued">No matcha type data yet</Text>
                )}
              </BlockStack>
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* ── Section 4: Streaks & Points ── */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Streak Distribution</Text>
                {streaksDist.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={streaksDist}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill={COLORS.darkGreen} name="Users" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Text as="p" tone="subdued">No streak data yet</Text>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Points by Reason</Text>
                {pointsByReason.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={pointsByReason}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="reason" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="issued" fill={COLORS.matcha} name="Issued" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="redeemed" fill={COLORS.pink} name="Redeemed" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Text as="p" tone="subdued">No points data yet</Text>
                )}
              </BlockStack>
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* ── Section 5: Referrals & Rewards ── */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Referral Funnel</Text>
                {funnelData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={funnelData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" name="Count" radius={[4, 4, 0, 0]}>
                        {funnelData.map((_entry, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Text as="p" tone="subdued">No referral data yet</Text>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">Top Redeemed Rewards</Text>
                {topRewardsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={topRewardsData} layout="vertical" margin={{ left: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip />
                      <Bar dataKey="count" fill={COLORS.orange} name="Redemptions" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Text as="p" tone="subdued">No rewards data yet</Text>
                )}
              </BlockStack>
            </Card>
          </InlineGrid>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
